import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Building2, TrendingUp, Users, CheckCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default case studies (used when database is empty)
const DEFAULT_CASE_STUDIES = [
  {
    id: "default-1",
    client: "Major FMCG Brand",
    industry: "FMCG & Snacks",
    challenge: "Pan-India retail presence expansion requiring 500+ floor standing units across 50 cities within 45 days.",
    solution: "Deployed foldable aluminum FSU units reducing logistics costs by 60%. Modular design enabled rapid deployment and easy installation.",
    results: [
      "500+ units delivered on time",
      "60% reduction in logistics costs",
      "40% increase in product visibility",
      "95% client satisfaction score"
    ],
    image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800"
  },
  {
    id: "default-2",
    client: "Premium Beverage Company",
    industry: "Beverages",
    challenge: "Summer campaign requiring custom-branded cooler displays for 200+ retail outlets with temperature control features.",
    solution: "Designed and manufactured insulated cooler bins with prominent branding, LED lighting, and easy-access design.",
    results: [
      "200+ cooler units installed",
      "35% increase in impulse purchases",
      "Temperature maintenance within ±2°C",
      "Extended campaign by 3 months due to success"
    ],
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800"
  },
  {
    id: "default-3",
    client: "International Tire Manufacturer",
    industry: "Automotive",
    challenge: "Nationwide dealership network requiring heavy-duty tire displays with specification boards and comparison charts.",
    solution: "Engineered extra-strong FSU units with powder-coated finish, integrated specification panels, and modular shelving.",
    results: [
      "300+ dealership displays deployed",
      "Enhanced product comparison experience",
      "Zero maintenance issues in first 2 years",
      "Repeated order for second phase"
    ],
    image: "https://images.unsplash.com/photo-1631194758628-71ec7c35137e?w=800"
  },
  {
    id: "default-4",
    client: "Leading Personal Care Brand",
    industry: "Personal Care & Cosmetics",
    challenge: "Premium counter-top displays for new product launch across 1,000+ modern trade outlets.",
    solution: "Created elegant CTU units with mirror panels, LED accent lighting, and tester product holders.",
    results: [
      "1,000+ CTU units manufactured",
      "Launch completed 1 week ahead of schedule",
      "45% increase in product trials",
      "Client awarded 'Best Product Launch Display'"
    ],
    image: "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=800"
  }
];

export default function CaseStudiesPage() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/case-studies`);
      // Use database case studies if available, otherwise use defaults
      setCaseStudies(response.data.length > 0 ? response.data : DEFAULT_CASE_STUDIES);
    } catch (error) {
      console.error("Error fetching case studies:", error);
      // Fall back to default case studies on error
      setCaseStudies(DEFAULT_CASE_STUDIES);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 to-secondary/10 border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope' }} data-testid="case-studies-heading">
              Success Stories
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Real-world results from enterprise clients who transformed their retail presence with HOKICO's innovative display solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-xl text-muted-foreground">Loading case studies...</div>
            </div>
          ) : (
            <div className="space-y-20">
              {caseStudies.map((study, index) => (
                <div key={study.id || index} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="mb-4">
                      <span className="inline-block px-4 py-1 bg-primary/10 text-primary text-sm font-semibold rounded-sm border border-primary/20 mb-4">
                        {study.industry}
                      </span>
                    </div>
                    <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>{study.client}</h2>

                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-bold text-primary mb-2">Challenge</h3>
                        <p className="text-muted-foreground leading-relaxed">{study.challenge}</p>
                      </div>

                      <div>
                        <h3 className="text-lg font-bold text-primary mb-2">Solution</h3>
                        <p className="text-muted-foreground leading-relaxed">{study.solution}</p>
                      </div>

                      <div>
                        <h3 className="text-lg font-bold text-primary mb-3">Results</h3>
                        <ul className="space-y-2">
                          {study.results.map((result, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                              <span className="text-muted-foreground">{result}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <div className="rounded-sm overflow-hidden border border-border/50 shadow-lg bg-gray-50 flex items-center justify-center p-4" style={{ minHeight: '400px' }}>
                      <img
                        src={study.image}
                        alt={study.client}
                        className="max-w-full max-h-[500px] object-contain"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800';
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Stats Impact */}
      <section className="py-20 bg-muted/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>Overall Impact</h2>
            <p className="text-lg text-muted-foreground">Measurable results across all client engagements</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-white border border-border/50 rounded-sm">
              <TrendingUp className="h-12 w-12 text-primary mx-auto mb-4" />
              <div className="text-4xl font-bold text-primary mb-2" style={{ fontFamily: 'Manrope' }}>40%</div>
              <div className="text-sm text-muted-foreground">Average Increase in Product Visibility</div>
            </div>
            <div className="text-center p-8 bg-white border border-border/50 rounded-sm">
              <Building2 className="h-12 w-12 text-primary mx-auto mb-4" />
              <div className="text-4xl font-bold text-primary mb-2" style={{ fontFamily: 'Manrope' }}>98%</div>
              <div className="text-sm text-muted-foreground">On-Time Project Delivery Rate</div>
            </div>
            <div className="text-center p-8 bg-white border border-border/50 rounded-sm">
              <Users className="h-12 w-12 text-primary mx-auto mb-4" />
              <div className="text-4xl font-bold text-primary mb-2" style={{ fontFamily: 'Manrope' }}>95%</div>
              <div className="text-sm text-muted-foreground">Client Satisfaction Score</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-secondary text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope' }}>Become Our Next Success Story</h2>
          <p className="text-xl mb-8">
            Let's discuss how we can transform your retail presence with custom POSM solutions.
          </p>
          <a href="mailto:sales@hokico.in">
            <button className="px-8 py-3 bg-white text-secondary font-semibold rounded-sm hover:bg-gray-100 transition-colors">
              Start Your Project
            </button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}